import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
    SessionReport,
    SessionReportHandoff,
    SessionReportMembership,
    SessionReportNotice,
    SessionReportRow,
    SessionReportReference,
    SessionReportSheetKey,
} from './types';

dayjs.extend(utc);
dayjs.extend(timezone);

/** 문구는 BACKEND_HANDOFF_STAGE1.md §4 를 따른다. 숫자는 서버 값을 그대로 끼워 넣기만 한다. */

/** 총회차·사용 회차는 서버가 둘 다 준 수강권만 표시한다. 월별 수업 수로 채우지 않는다. */
export const hasMembershipSessionUsage = (m: SessionReportMembership): m is SessionReportMembership & { totalSession: number; usedSessions: number } =>
    !m.isSessionless && typeof m.totalSession === 'number' && typeof m.usedSessions === 'number';

export const describeMembershipSessionUsage = (m: SessionReportMembership, isExpired: boolean) => {
    if (!hasMembershipSessionUsage(m)) return null;
    const total = m.totalSession.toLocaleString('ko-KR');
    const used = m.usedSessions.toLocaleString('ko-KR');
    const remaining = m.remaining.toLocaleString('ko-KR');
    if (m.remaining < 0) return `총회차는 ${total}회인데 수업은 ${used}회예요`;
    if (isExpired && m.remaining > 0) {
        return m.expiryPolicy === 'COUNT'
            ? `총회차 ${total}회 중 ${used}회를 쓰고 ${remaining}회 남았어요 · 기간이 지나도 쓸 수 있어요`
            : `총회차 ${total}회 중 ${used}회를 쓰고 ${remaining}회 남은 채로 기간이 끝났어요`;
    }
    return `${total}회 중 ${used}회 썼어요 · ${remaining}회 남음`;
};

/** 초과 회차를 다시 계산하지 않고 서버의 음수 잔여를 양수 문구로만 표시한다. */
export const describeMembershipSessionOverage = (m: SessionReportMembership) =>
    hasMembershipSessionUsage(m) && m.remaining < 0
        ? `총회차 ${m.totalSession.toLocaleString('ko-KR')}회보다 수업이 ${Math.abs(m.remaining).toLocaleString('ko-KR')}회 많아요(${m.usedSessions.toLocaleString('ko-KR')}/${m.totalSession.toLocaleString('ko-KR')})`
        : null;

export const SESSION_REPORT_SHEETS: { key: SessionReportSheetKey; title: string; shortTitle: string }[] = [
    { key: 'valid', title: '유효기간내', shortTitle: '내' },
    { key: 'expired', title: '유효기간외', shortTitle: '외' },
    { key: 'ghost', title: '유령', shortTitle: '유령' },
];

/**
 * 화면 이름. 엑셀 시트 이름(title)은 고객 원본(「유효기간내/외/유령」)과 맞춰야 해서 그대로 두고, 화면에서는 쉬운 말로 부른다.
 */
export const SESSION_REPORT_SHEET_SCREEN_TITLE: Record<SessionReportSheetKey, string> = {
    valid: '유효기간 안',
    expired: '유효기간 밖',
    ghost: '장기 미출석',
};

/** ISO(UTC) → KST 표기. 서버 시각은 UTC 문자열로 온다. */
export const formatKst = (iso: string | null | undefined, format = 'YYYY-MM-DD HH:mm') => {
    if (!iso) return '';
    const d = dayjs(iso);
    if (!d.isValid()) return '';
    return d.tz('Asia/Seoul').format(format);
};

/**
 * 인계 항목 종류 이름(계약 §0-11). 1:2 대표 이동(`companionRepMove`)은 인계·양도와 구분해 보낸 쪽(−)·받은 쪽(+)으로 부른다.
 * 모르는 type 은 「이동」으로 보이고, 항목 표기는 서버 counterpartLabel 을 그대로 쓰므로 깨지지 않는다.
 */
export const COMPANION_REP_MOVE_TYPE = 'companionRepMove';
export const COMPANION_REP_MOVE_LABEL = '1:2 대표 이동';
const HANDOFF_TYPE_LABEL: Record<string, string> = {
    handoff: '인계',
    transfer: '양도',
    [COMPANION_REP_MOVE_TYPE]: COMPANION_REP_MOVE_LABEL,
};
export const describeHandoffType = (type: string | undefined, sessions?: number) => {
    if (type === COMPANION_REP_MOVE_TYPE) {
        if (sessions === undefined || sessions === 0) return COMPANION_REP_MOVE_LABEL;
        return `${COMPANION_REP_MOVE_LABEL} ${sessions < 0 ? '보냄' : '받음'}`;
    }
    return (type && HANDOFF_TYPE_LABEL[type]) || '이동';
};
export const isCompanionRepMove = (h: Pick<SessionReportHandoff, 'type'>) => h.type === COMPANION_REP_MOVE_TYPE;

/**
 * 다른 센터 흔적은 화면·엑셀에 내보내지 않는다(10/19 사용자 결정). 서버가 상대 이름 대신 「다른 센터」를 싣던
 * 구버전 응답이면 상대 이름을 비우고 종류 이름(인계·양도)으로 보인다. 회차 수는 이 센터 수강권 자신의 값이라 그대로 둔다.
 */
const OTHER_CENTER_WORD = '다른 센터';
export const visibleCounterpartLabel = (label: string | null | undefined) =>
    label && !label.includes(OTHER_CENTER_WORD) ? label : '';

/**
 * 「김성윤 -40」 — 고객 총괄 파일과 같은 모양(받은 쪽은 부호 없이). 라벨이 없으면 종류 이름.
 * 1:2 대표 이동은 인계로 보이지 않게 「1:2 대표 이동 보냄 · 김성윤 -7」.
 */
export const formatHandoffEntry = (h: SessionReportHandoff) => {
    const counterpart = visibleCounterpartLabel(h.counterpartLabel);
    if (isCompanionRepMove(h)) {
        const who = counterpart ? ` · ${counterpart}` : '';
        return `${describeHandoffType(h.type, h.sessions)}${who} ${h.sessions}`;
    }
    return `${counterpart || describeHandoffType(h.type)} ${h.sessions}`;
};

export const formatHandoffs = (handoffs: SessionReportHandoff[] | undefined, separator = ', ') =>
    (handoffs ?? []).map(formatHandoffEntry).join(separator);

const NOTICE_TEXT: Record<string, (n: number) => string> = {
    CHANGED_AFTER_MONTH_END: (n) => `월말이 지난 뒤 입력·취소된 이 달 기록이 ${n}건 있어요`,
    HANDOFF_NET_NOT_ZERO: (n) => `센터 안 인계·양도 합계가 맞지 않아요(${n}회). 확인이 필요한 인계·양도가 있어요`,
    UNLINKED_HANDOFF: (n) => `예전 방식으로 강사를 바꿔서, 바꾸기 전 수강권과의 연결 기록이 없는 수강권이 ${n}건 있어요(남은 회차는 맞아요)`,
    DELETED_PAYMENT_ROWS_EXCLUDED: (n) => `삭제된 결제 기록 ${n}건은 빼고 계산했어요(매출 목록과 다를 수 있어요)`,
    TRANSFER_ANOMALY: (n) => `이 달에 시작한 양도 중 끝나지 않았거나 한쪽만 반영된 것이 ${n}건 있어요`,
    LEGACY_CANCELED_PAYMENT_ROWS: (n) => `예전 방식으로 취소된 변경(회차 추가)·환불 ${n}건은 회차 계산에서 뺐어요`,
    UNPAID_UNKNOWN: (n) => `미수를 계산할 수 없는 수강권이 ${n}건 있어요. 회원별 표에 「확인 필요」로 보여요`,
    LINEAGE_INVALID: (n) => `인계 연결을 확인해야 하는 수강권이 ${n}장 있어요. 인계 전후를 잇지 않고 따로 셌어요`,
    // 서버 10/19: 결제 조회 인덱스 미준비 등으로 일부 환불 기록을 못 본 경우(count 1/0). 0 이면 숨는다.
    PAYMENT_LOOKUP_INCOMPLETE: () => '일부 환불 기록을 확인하지 못했어요. 잠시 후 다시 확인해 주세요',
    LESSON_MOVED_AFTER_CUTOFF: (n) => `이 달 숫자를 낸 뒤에 다른 수강권으로 옮긴 수업이 ${n}건 있어요. 급여를 이미 드렸다면 차액을 맞춰 주세요`,
    LESSON_MOVE_SOURCE_UNAVAILABLE: () => '옮겨 나간 쪽 표시는 지금 볼 수 없어요(옮겨 받은 쪽에는 표시돼요)',
    // 다른 센터 관련 공지(EXTERNAL_PAYMENT_CHECK_SKIPPED)는 문구를 두지 않는다(10/19) — 구서버가 보내면 「기타 확인 필요」.
};

/** 회원 표 밖에서도 바로 보여야 하는 공지(0 이면 숨김). */
export const PROMINENT_NOTICE_CODES = [
    'UNPAID_UNKNOWN',
    'LINEAGE_INVALID',
    'PAYMENT_LOOKUP_INCOMPLETE',
];

export const describeProminentNotices = (notices: SessionReportNotice[] | undefined): string[] =>
    describeNotices((notices ?? []).filter((n) => PROMINENT_NOTICE_CODES.includes(n.code)));

/**
 * 화면·엑셀에서 **아예 무시하는** 공지(「기타 확인 필요」로도 세지 않는다).
 * 센터 미지정 수강권 개수는 이 센터 귀속을 확인하지 못한 값이라 다른 센터 수강권이 섞일 수 있다(9회차 지적 1, 서버도 응답에서 뺀다).
 * 연결 못 한 결제·양도 기록 수(UNRESOLVED_RECORDS_EXCLUDED)도 같은 이유로 무시한다(10회차 — 서버도 응답에서 뺀다).
 * 구서버가 보내도 개수가 나오지 않게 한다.
 */
export const IGNORED_NOTICE_CODES = new Set([
    'EXCLUDED_UNASSIGNED_LEGACY',
    'LEGACY_NULL_CENTER_MEMBERSHIPS',
    'UNRESOLVED_RECORDS_EXCLUDED',
]);

const noticeValue = (n: SessionReportNotice) => (typeof n.count === 'number' ? n.count : n.sessions ?? 0);

/** 값이 0 인 공지는 숨긴다. 모르는 코드(서버가 나중에 추가할 수 있다)는 영문 대신 「기타 확인 필요 n건」. */
export const describeNotices = (notices: SessionReportNotice[] | undefined): string[] =>
    (notices ?? [])
        .filter((n) => !IGNORED_NOTICE_CODES.has(n.code) && noticeValue(n) !== 0)
        .map((n) =>
            NOTICE_TEXT[n.code] ? NOTICE_TEXT[n.code](noticeValue(n)) : `${UNKNOWN_FLAG_TEXT} ${noticeValue(n)}건`,
        );

export const FIXED_GUIDES = [
    '매출 목록과 금액이 다를 수 있어요. 회차 추가 결제는 「변경」으로 따로 보이고, 처음 결제의 환불은 회차를 줄인 지금 담당 강사에게 들어가요. 삭제된 결제는 빼요',
    '미수는 지금 기준 값이에요(지난 달을 다시 봐도 지금 값이에요)',
    '결제 없이 고친 회차(수강권 직접 수정·시작 회차 정정)는 전월 잔여에 반영돼 따로 드러나지 않아요',
];

const FLAG_TEXT: Record<string, string> = {
    NEGATIVE_REMAINING: '수업이 회차보다 많아 남은 회차가 마이너스예요',
    LESSON_BEFORE_START: '발급 전에 한 수업이 있어요',
    OTHER_COACH_LESSON: '다른 강사가 한 수업이 들어 있어요',
    ISSUE_KIND_MISMATCH: '신규/재등록 표시와 발급 기록이 달라요',
    END_ESTIMATED: '삭제된 시각을 몰라 마지막 수업·양도 때 없어진 것으로 계산했어요',
    POST_END_SESSION_CHANGE: '끝난 뒤 회차가 바뀌었어요(남은 회차엔 영향이 없고 금액만 반영했어요)',
    UNLINKED_HANDOFF: '예전 방식으로 강사를 바꿔서, 바꾸기 전 수강권과의 연결 기록이 없어요. 남은 회차는 맞아요',
    HANDOFF_CLEANUP_INCOMPLETE: '강사를 바꿨는데 바꾸기 전 수강권이 닫히지 않고 남아 있어요. 그래서 남은 회차가 두 번 세어져요',
    HANDOFF_COPY_NOT_FOUND: '강사를 바꾸며 새로 만든 수강권을 찾지 못했어요',
    // 서버 10/19: 원본 회차가 줄었는지 확인되지 않은 양도(sourceDecrementOutcome UNKNOWN)도 이 코드로 온다.
    TRANSFER_UNPROCESSED: '양도 중 오류가 나서 보낸 쪽 수강권 회차가 줄었는지 확인이 필요해요',
    TRANSFER_TARGET_NO_EFFECT: '양도받은 수강권이 아직 쓸 수 있는 상태가 아니에요(남은 회차에 넣지 않았어요)',
    COMPANION_COUNTERPART_CHANGE: '1:2 상대 수강권의 회차가 바뀌었거나 혼자 한 수업이 있어요',
    COMPANION_PRIMARY_NOT_ALIVE: '집계 시점에 남아 있는 1:2 대표 수강권이 없어요',
    SESSIONLESS: '무제한 수강권이라 회차 합계에서 뺐어요',
    SERVICE_SESSION: '서비스(무료) 회차예요',
    CORRECTED_AFTER_CUTOFF: '집계 시점 뒤에 회차·시작 회차·만료일을 고친 기록이 있어요',
    LEGACY_CANCELED_PAYMENT_ROW: '예전 방식으로 취소된 결제는 뺐어요',
    CANCELED_AT_MISSING: '발급 취소 시각이 없어 추정했어요',
    END_BEFORE_START: '끝난 날이 시작한 날보다 앞서요(기록 확인이 필요해요)',
    COMPANION_DUPLICATE_LESSON: '같은 시각 중복 수업 — 진행은 1회로 셌어요(중복 일정 확인)',
    ISSUE_DATE_AMBIGUOUS: '발급일을 고쳤는지 확인이 필요해요 — 삭제된 첫 결제일로 계산했어요',
    LINEAGE_INVALID: '강사를 바꾸기 전과 후의 수강권 연결 기록이 맞지 않아요',
    UNPAID_UNKNOWN: '미수를 계산할 수 없어요 — 수강권 결제 기록을 확인해 주세요',
    // 계약 §0-10·§4-3(서버 01a1b003). 넷 다 확인 필요로 센다. SCHEDULE_* 는 검산 차이의 원인 표시.
    COMPANION_REPRESENTATIVE_CHANGED: '1:2 대표 수강권이 지난달과 달라요 — 전월 잔여가 지난달 남은 회차와 다를 수 있어요',
    SCHEDULE_AFTER_END: '수강권 종료 뒤 수업이 있어요 — 발급 취소·인계로 끝난 뒤의 수업이에요(계산이 안 맞는 이유예요)',
    SCHEDULE_BEFORE_ISSUE: '발급 전 수업이 있어요 — 이 달 발급 수강권에 지난달 이전 수업이 있어요(소급 등록 등)',
    COMPANION_DATA_INCONSISTENT: '1:2 대표 표시가 둘이에요 — 수강권 등록 순서로 대표를 골랐어요',
    // 서버 10/19(F5). 확인 필요로 센다.
    // 다른 센터 관련 표시(OTHER_CENTER_LESSON·OTHER_CENTER_PAYMENT·EXTERNAL_CENTER_TRANSFER)는 문구를 두지 않는다(10/19 사용자 결정 —
    // 다른 센터 흔적을 화면·엑셀에 내지 않는다). 서버도 응답에서 빼고, 구서버가 보내면 「기타 확인 필요」로 보인다.
    DELETED_AFTER_PERIOD: '이 달 수업 중 월말이 지난 뒤 삭제된 것이 있어요. 지난번에 받은 엑셀과 숫자가 다를 수 있어요',
    LESSON_MOVED_AFTER_CUTOFF: '이 달 숫자를 낸 뒤에 수업을 다른 수강권으로 옮긴 기록이 있어요. 수업료·급여가 처음 숫자와 달라졌을 수 있어요',
};

/** 사전에 없는 코드(서버가 나중에 더한 것)는 영문 코드 대신 이 문구로 보인다. */
export const UNKNOWN_FLAG_TEXT = '기타 확인 필요';

/**
 * 화면·엑셀에서 무시하는 플래그. 센터 미지정 표시(LEGACY_NULL_CENTER)는 보고서 대상이 「이 센터」 수강권으로 바뀐 뒤 항상 0 이고,
 * 문구는 다른 센터 흔적이 될 수 있어 보이지 않는다(9회차). 구서버가 보내도 문구·배지·칩·확인 필요 판정에 쓰지 않는다.
 */
export const IGNORED_FLAGS = new Set(['LEGACY_NULL_CENTER']);
/** 화면·엑셀에 쓸 플래그만(무시 대상 제외). */
export const visibleFlags = (flags: string[] | undefined) => (flags ?? []).filter((f) => !IGNORED_FLAGS.has(f));

/** 안내용 플래그 — 이것만 있는 행은 「확인 필요」가 아니다(서버 checkNeededRowCount 와 같은 기준, 계약 §0-9). */
// 옛 인계(UNLINKED_HANDOFF)는 남은 회차가 맞고 할 일이 없어 확인 필요에서 뺀다(10/20 사장님 결정, 서버 sessionReport.ts 와 같은 목록).
// 10/26 대표 결정: 숫자가 맞으면 「발급 전 수업」(먼저 수업하고 나중에 등록)과 할 일 없는(info) 표시도 확인 필요가 아니다.
// 운영 실측(한 센터 9월) 확인 필요 25건 중 16건이 이것이었다. 숫자가 다르면 MISMATCH 로 남는다. 서버와 같은 목록.
export const INFO_ONLY_FLAGS = new Set([
    'SESSIONLESS',
    'SERVICE_SESSION',
    'UNLINKED_HANDOFF',
    'LESSON_BEFORE_START',
    'SCHEDULE_BEFORE_ISSUE',
    'OTHER_COACH_LESSON',
    'HANDOFF_COPY_NOT_FOUND',
    'END_ESTIMATED',
    'POST_END_SESSION_CHANGE',
    'LEGACY_CANCELED_PAYMENT_ROW',
    'CANCELED_AT_MISSING',
    'COMPANION_REPRESENTATIVE_CHANGED',
    'CORRECTED_AFTER_CUTOFF',
    'DELETED_AFTER_PERIOD',
]);

/**
 * 「확인 필요」 행 = 검산 차이(MISMATCH)이거나 안내용이 아닌 플래그가 있는 행. 모르는 플래그도 확인 필요로 본다
 * (서버가 나중에 추가할 수 있고, 서버 건수도 안내용 두 개만 뺀다). 미수 null 도 포함한다.
 */
export const isCheckNeededRow = (row: Pick<SessionReportRow, 'check' | 'unpaidAmount' | 'resolved'>) =>
    row.resolved !== true && (row.check?.status === 'MISMATCH' ||
    visibleFlags(row.check?.flags).some((f) => !INFO_ONLY_FLAGS.has(f)) ||
    row.unpaidAmount === null);

/** 짧은 이름 — 행 배지·총괄 칩에 쓴다. 모르는 코드는 「기타 확인 필요」. */
const FLAG_SHORT: Record<string, string> = {
    LINEAGE_INVALID: '강사 변경 기록 확인',
    UNPAID_UNKNOWN: '미수 계산 불가',
    COMPANION_DUPLICATE_LESSON: '중복 일정',
    ISSUE_DATE_AMBIGUOUS: '발급일 확인',
    NEGATIVE_REMAINING: '남은 회차 마이너스',
    LESSON_MOVED_AFTER_CUTOFF: '집계 뒤 수업 옮김',
    LESSON_BEFORE_START: '발급 전 수업',
    OTHER_COACH_LESSON: '다른 강사 수업',
    ISSUE_KIND_MISMATCH: '신규·재등록 불일치',
    END_ESTIMATED: '삭제 시각 추정',
    POST_END_SESSION_CHANGE: '종료 뒤 변동',
    UNLINKED_HANDOFF: '예전 강사 변경',
    HANDOFF_CLEANUP_INCOMPLETE: '강사 변경 정리 안 됨',
    HANDOFF_COPY_NOT_FOUND: '바뀐 강사 수강권 없음',
    TRANSFER_UNPROCESSED: '양도 미완료',
    TRANSFER_TARGET_NO_EFFECT: '양도 미반영',
    COMPANION_COUNTERPART_CHANGE: '1:2 상대 변동',
    COMPANION_PRIMARY_NOT_ALIVE: '1:2 대표 없음',
    CORRECTED_AFTER_CUTOFF: '이후 정정',
    LEGACY_CANCELED_PAYMENT_ROW: '옛 취소 결제',
    CANCELED_AT_MISSING: '취소 시각 추정',
    END_BEFORE_START: '기록 확인',
    COMPANION_REPRESENTATIVE_CHANGED: '1:2 대표 변경',
    SCHEDULE_AFTER_END: '종료 뒤 수업',
    SCHEDULE_BEFORE_ISSUE: '발급 전 수업(이전 달)',
    COMPANION_DATA_INCONSISTENT: '1:2 대표 중복',
    DELETED_AFTER_PERIOD: '월말 뒤 삭제',
};
export const describeFlagShort = (flag: string) => FLAG_SHORT[flag] ?? UNKNOWN_FLAG_TEXT;

/** 검산 차이가 아닌 「확인 필요」 행의 짧은 배지 문구. */
export const attentionBadgeText = (row: Pick<SessionReportRow, 'check' | 'unpaidAmount'>): string | null => {
    const flag = visibleFlags(row.check?.flags).find((f) => !INFO_ONLY_FLAGS.has(f));
    if (flag) return describeFlagShort(flag);
    return row.unpaidAmount === null ? describeFlagShort('UNPAID_UNKNOWN') : null;
};

/** 강사·센터의 「확인 필요」 줄 수 — 서버 checkNeededRowCount, 구서버면 mismatchRowCount(계약 §0-9). */
type ReviewReference = Partial<SessionReportReference> | undefined;

/** 서버 집계끼리의 차이만 표시한다. 회차·금액은 계산하지 않는다. */
export const subtractResolvedCount = (count: number, resolved: number | undefined, label: string) => {
    if (resolved === undefined) return count;
    const remaining = count - resolved;
    if (remaining < 0) console.warn(`세션 현황 ${label}: 처리 완료 건수가 전체 건수보다 많아요.`, { count, resolved });
    return Math.max(0, remaining);
};

export const mismatchCount = (reference: ReviewReference) =>
    subtractResolvedCount(reference?.mismatchRowCount ?? 0, reference?.resolvedMismatchRowCount, '검산 차이');

// 엑셀은 처리 기록과 무관하게 서버 원래 건수를 사용해요.
export const checkNeededCount = (reference: ReviewReference) => reference?.checkNeededRowCount ?? reference?.mismatchRowCount ?? 0;

/** 화면의 손볼 일에만 처리 완료 건수를 차감해요. */
export const screenCheckNeededCount = (reference: ReviewReference) => reference?.checkNeededRowCount !== undefined
    ? subtractResolvedCount(reference.checkNeededRowCount, reference.resolvedCheckNeededRowCount, '확인 필요')
    : mismatchCount(reference);

export const checkFlagCounts = (reference: ReviewReference): Record<string, number> => Object.fromEntries(
    Array.from(new Set([...Object.keys(reference?.checkFlagCounts ?? {}), ...Object.keys(reference?.resolvedCheckFlagCounts ?? {})]))
        .map((flag) => [flag, INFO_ONLY_FLAGS.has(flag) || flag === 'UNPAID_UNKNOWN' ? reference?.checkFlagCounts?.[flag] ?? 0
            : subtractResolvedCount(reference?.checkFlagCounts?.[flag] ?? 0, reference?.resolvedCheckFlagCounts?.[flag], flag)]),
);

/** 미수 확인은 처리 완료와 무관하게 서버 원래 건수를 표시해요. */
export const unpaidReviewCount = (reference: ReviewReference) =>
    reference?.checkFlagCounts?.UNPAID_UNKNOWN || reference?.unpaidUnknownRowCount || 0;

/** 사전에 없는 플래그들을 하나로 묶은 칩의 키. */
export const UNKNOWN_FLAG_KEY = '__unknown';

/** 칩 키(플래그 또는 「기타 확인 필요」 묶음)에 해당하는 행이 이 강사에게 있는가 — 칩을 누르면 갈 강사를 고른다. */
export const coachHasCheckFlag = (reference: ReviewReference, key: string) => {
    // 미수 칩 건수는 그대로 두되, 손볼 표로 이동할 때는 미완료 회원이 있는 강사를 골라요.
    if (key === 'UNPAID_UNKNOWN') return subtractResolvedCount(reference?.checkFlagCounts?.UNPAID_UNKNOWN ?? 0, reference?.resolvedCheckFlagCounts?.UNPAID_UNKNOWN, '미수 확인') > 0;
    const counts = checkFlagCounts(reference);
    if (key !== UNKNOWN_FLAG_KEY) return (counts[key] ?? 0) > 0;
    return Object.entries(counts).some(([flag, n]) => n > 0 && !INFO_ONLY_FLAGS.has(flag) && !IGNORED_FLAGS.has(flag) && !FLAG_SHORT[flag]);
};

/**
 * 총괄 칩용 플래그 목록(서버 checkFlagCounts, 안내용·0 제외, 많은 순).
 * 사전에 없는 코드는 「기타 확인 필요」 한 칩으로 합친다(같은 이름 칩이 여러 개 보이지 않게).
 */
export const listCheckFlags = (reference: ReviewReference) => {
    const known: { flag: string; count: number; label: string }[] = [];
    let unknownCount = 0;
    for (const [flag, n] of Object.entries(checkFlagCounts(reference))) {
        if (!(n > 0) || INFO_ONLY_FLAGS.has(flag) || IGNORED_FLAGS.has(flag)) continue;
        if (FLAG_SHORT[flag]) known.push({ flag, count: n, label: FLAG_SHORT[flag] });
        else unknownCount += n;
    }
    if (unknownCount > 0) known.push({ flag: UNKNOWN_FLAG_KEY, count: unknownCount, label: UNKNOWN_FLAG_TEXT });
    return known.sort((a, b) => b.count - a.count);
};

/** 미수 null = 서버가 계산하지 않은 값. 0 으로 바꾸지 않는다. */
export const UNPAID_UNKNOWN_TEXT = '확인 필요';
export const isUnpaidUnknown = (row: Pick<SessionReportRow, 'unpaidAmount'>) => row.unpaidAmount === null;

/** 무제한권(999+) 별도 행. 같은 회원의 일반권 행과 합치지 않는다. */
export const isUnlimitedRow = (row: Pick<SessionReportRow, 'rowKey'>) => row.rowKey.endsWith('|unlimited');

/** 모르는 flag 는 영문 코드 대신 「기타 확인 필요」. */
export const describeFlag = (flag: string) => FLAG_TEXT[flag] ?? UNKNOWN_FLAG_TEXT;

export const isMismatchRow = (row: SessionReportRow) => row.check?.status === 'MISMATCH';

/**
 * 「확인」 칸 문구 — 서버가 계산한 diff 와 flags 를 그대로 옮긴다.
 * MISMATCH 가 아니어도 flags 가 있으면 참고 표시로 쓴다.
 */
/** 1:2 대표가 다른 강사로 옮겨간 달의 이전 강사 쪽 행(계약 §0-11). 수강권 상세는 새 강사 쪽에 있다. */
export const isRepMovedRow = (row: Pick<SessionReportRow, 'rowKey'>) => /\|repMoved$/.test(row.rowKey ?? '');
export const REP_MOVED_FLAG_TEXT = '1:2 대표 수강권이 다른 강사로 바뀌었어요';
export const REP_MOVED_DETAIL_TEXT = '1:2 대표 수강권이 다른 강사로 옮겨갔어요 — 남은 회차는 새 대표 강사 쪽에서 이어져요';
/** 이동 행 상세 안내 — 상대 강사 이름은 이 행의 「1:2 대표 이동 보냄」 항목에서 가져온다. */
export const describeRepMoved = (row: Pick<SessionReportRow, 'handoffs'>) => {
    const sent = (row.handoffs ?? []).find((h) => isCompanionRepMove(h) && h.sessions < 0);
    const who = visibleCounterpartLabel(sent?.counterpartLabel);
    if (!who) return REP_MOVED_DETAIL_TEXT;
    return `1:2 대표 수강권이 ${who} 강사로 옮겨갔어요 — 남은 회차는 ${who} 강사 쪽에서 이어져요`;
};

/** 행 문맥에 맞춘 플래그 문구 — 대표 이동 행의 COMPANION_REPRESENTATIVE_CHANGED 만 다르게(§0-11). */
export const describeRowFlag = (row: Pick<SessionReportRow, 'rowKey'>, flag: string) =>
    flag === 'COMPANION_REPRESENTATIVE_CHANGED' && isRepMovedRow(row) ? REP_MOVED_FLAG_TEXT : describeFlag(flag);

/** 무제한권 행(서버가 세션 열·검산값을 0 으로 두고 검산하지 않는다 — 계약 §3). */
export const isSessionlessRow = (row: Pick<SessionReportRow, 'rowKey' | 'check'>) =>
    (row.check?.flags ?? []).includes('SESSIONLESS') || /\|unlimited$/.test(row.rowKey ?? '');

/**
 * 화면에 IN·OUT 열(과 「근무시간 미설정」 표시)을 보일지(10/19 범용화).
 * 서버는 진행 = IN + OUT 으로 세고, 근무시간이 없으면 전부 IN 이다. 그래서 보고서 전체에 OUT 이 한 건도 없으면 IN 은 진행과
 * 같은 숫자를 되풀이할 뿐이다 — 수업료를 IN/OUT 으로 나누지 않는 일반 센터 대부분이 여기에 든다. 그때만 숨긴다.
 * 판정은 화면 기준 보고서(요약) 하나로 한다 — 강사 요약 표와 회원별 표가 같은 열을 갖게. 엑셀은 원본 양식 자리를 지키므로 이 값을 보지 않는다.
 */
export const hasInOutSplit = (report: Pick<SessionReport, 'centerTotals' | 'coaches'>) =>
    (report.centerTotals?.outCount ?? 0) > 0 || report.coaches.some((c) => (c.summary?.outCount ?? 0) > 0);

export const SESSIONLESS_CHECK_TEXT = '무제한권이라 검산 제외';
export const WORK_ROUTINE_MISSING_TEXT = '근무시간이 설정되지 않아 전부 IN으로 셌어요';

export const describeRowCheck = (row: SessionReportRow, opts: { workRoutineMissing?: boolean } = {}): string => {
    const parts: string[] = [];
    const sessionless = isSessionlessRow(row);
    if (sessionless) parts.push(SESSIONLESS_CHECK_TEXT);
    else if (isMismatchRow(row)) parts.push(`차이 ${row.check.diff}`);
    for (const flag of visibleFlags(row.check?.flags)) {
        if (sessionless && flag === 'SESSIONLESS') continue;
        parts.push(describeRowFlag(row, flag));
    }
    if (opts.workRoutineMissing) parts.push(WORK_ROUTINE_MISSING_TEXT);
    return parts.join(' · ');
};

/** 화면 상단·엑셀 2행 기준 시각 안내(§4-1). */
export const describeBasis = ({
    yearMonth,
    generatedAt,
    changedAfterMonthEnd,
}: {
    yearMonth: string;
    generatedAt: string;
    changedAfterMonthEnd: number;
}) => {
    const [y, m] = yearMonth.split('-');
    return `${y}년 ${Number(m)}월 · ${formatKst(generatedAt)}에 지금 기록으로 다시 계산한 값이에요. 월말이 지난 뒤 입력·수정·취소된 기록이 있으면 그 달에 뽑았던 숫자와 다를 수 있어요(이번 계산에서 찾은 월말 뒤 변경 ${changedAfterMonthEnd}건). 저장된 마감 값이 아니에요.`;
};

/**
 * 정상 조회 뒤 같은 조건 「다시 계산」이 실패한 상태(11회차 지적 2). React Query 는 오류여도 직전 성공 자료를 data 로 남기고
 * isPreviousData 도 false 라, 안내가 없으면 옛 결과가 새 계산처럼 보인다. 안내 문구와 엑셀을 막는 이유를 만든다.
 * 오류 문구는 503(시간 초과·기록이 많음)이면 그 오류의 문구, 그 밖이면 일반 문구.
 */
export const REFETCH_FAILED_EXPORT_BLOCKED_TEXT = '다시 계산하지 못했어요. 「다시 계산」이 성공하면 받을 수 있어요';
export const describeSummaryRefetchFailure = ({
    hasReport,
    isError,
    isFetching,
    error,
    generatedAt,
    isTimeout,
}: {
    hasReport: boolean;
    isError: boolean;
    isFetching: boolean;
    error: unknown;
    generatedAt?: string;
    /** 503 계열(SessionReportTimeoutError)이면 그 문구를 쓴다 */
    isTimeout: boolean;
}): { title: string; detail: string } | null => {
    if (!hasReport || !isError || isFetching) return null;
    const detail = isTimeout && error instanceof Error && error.message ? error.message : '잠시 후 다시 시도해 주세요.';
    const at = formatKst(generatedAt, 'H시 m분');
    return {
        title: at ? `다시 계산하지 못했어요. 아래는 ${at} 기준 이전 결과예요` : '다시 계산하지 못했어요. 아래는 이전 결과예요',
        detail,
    };
};

export const getNoticeCount = (notices: SessionReportNotice[] | undefined, code: string) => {
    const n = (notices ?? []).find((x) => x.code === code);
    return n ? noticeValue(n) : 0;
};

/**
 * 화면 표시만의 우선순위. 확인 필요 판정·서버 건수·엑셀 문구와는 별개예요.
 * 기준은 「관리자가 관리자 웹에서 직접 고칠 수 있는가」(10/20 조사):
 *  - fix: 관리자 웹으로 고치면 다음 계산에서 사라진다(회차 수정·일정 삭제)
 *  - ask: 고칠 화면이 없다(인계·양도 연결) — 라포에 알려야 한다
 *  - review: 기록이 맞는지 한 번 보면 된다. 대부분 정상 운영이다
 *  - info: 할 일이 없다(예전 데이터 흔적·안내)
 */
export type ReviewLevel = 'fix' | 'ask' | 'review' | 'info';
export const REVIEW_LEVEL_ORDER: ReviewLevel[] = ['fix', 'ask', 'review', 'info'];
export const FLAG_REVIEW_LEVEL: Record<string, ReviewLevel> = {
    NEGATIVE_REMAINING: 'fix',
    COMPANION_DUPLICATE_LESSON: 'fix',
    HANDOFF_CLEANUP_INCOMPLETE: 'ask',
    TRANSFER_UNPROCESSED: 'ask',
    LINEAGE_INVALID: 'ask',
    UNPAID_UNKNOWN: 'ask',
    // 계산이 안 맞는 것 자체는 관리자가 고칠 화면이 없다 — 같은 회원의 다른 이유를 먼저 처리하고, 남으면 문의.
    __mismatch: 'ask',
    // 10/26: 먼저 수업하고 나중에 등록한 경우라 숫자가 맞으면 할 일이 없다. 숫자가 다르면 행은 「계산이 안 맞음」으로 따로 뜬다.
    LESSON_BEFORE_START: 'info',
    SCHEDULE_BEFORE_ISSUE: 'info',
    SCHEDULE_AFTER_END: 'review',
    // 정산 기준 정정 창은 상품 분류를 쓰는 센터에서만 열린다(settlementBasis) — 어디서나 고칠 수 있다고 하지 않는다.
    ISSUE_KIND_MISMATCH: 'review',
    ISSUE_DATE_AMBIGUOUS: 'review',
    END_BEFORE_START: 'review',
    TRANSFER_TARGET_NO_EFFECT: 'review',
    COMPANION_COUNTERPART_CHANGE: 'review',
    COMPANION_PRIMARY_NOT_ALIVE: 'review',
    COMPANION_DATA_INCONSISTENT: 'review',
    LESSON_MOVED_AFTER_CUTOFF: 'review',
    OTHER_COACH_LESSON: 'info',
    UNLINKED_HANDOFF: 'info',
    HANDOFF_COPY_NOT_FOUND: 'info',
    END_ESTIMATED: 'info',
    POST_END_SESSION_CHANGE: 'info',
    LEGACY_CANCELED_PAYMENT_ROW: 'info',
    CANCELED_AT_MISSING: 'info',
    COMPANION_REPRESENTATIVE_CHANGED: 'info',
    CORRECTED_AFTER_CUTOFF: 'info',
    DELETED_AFTER_PERIOD: 'info',
    SESSIONLESS: 'info',
    SERVICE_SESSION: 'info',
};
/** 사전에 없는 플래그(서버가 나중에 더한 것)는 한 번 보기 — 고칠 수 있는지 모르니 「직접 고치기」로 몰지 않는다. */
export const flagReviewLevel = (flag: string): ReviewLevel => FLAG_REVIEW_LEVEL[flag] ?? 'review';

const ASK_RAPPO = '관리자 화면에서는 고칠 수 없어요. 라포 고객센터에 회원 이름을 알려 주시면 정리해 드려요.';
const FLAG_ACTION: Record<string, string> = {
    // 보고서의 남은 회차는 고른 달 기준이고 수강권 여러 장의 합이라, 지금 마이너스인지는 화면이 단정할 수 없다(10/20 3회차 검증).
    NEGATIVE_REMAINING: '같은 회원에 인계처럼 다른 이유가 있으면 그것부터 봐 주세요. 그다음 수강권 정보에서 지금 남은 회차를 확인해 주세요. 지금도 마이너스면 「수업 보기」에서 다른 수강권에 차감돼야 할 수업을 맞는 수강권으로 옮기거나, 회차를 추가 결제받거나(업그레이드), 잘못 잡힌 수업을 스케줄에서 지워 주세요. 이미 풀렸으면 그대로 두세요.',
    COMPANION_DUPLICATE_LESSON: '스케줄에서 같은 시각에 두 번 잡힌 수업 중 하나를 지워 주세요.',
    HANDOFF_CLEANUP_INCOMPLETE: ASK_RAPPO,
    TRANSFER_UNPROCESSED: ASK_RAPPO,
    LINEAGE_INVALID: ASK_RAPPO,
    UNPAID_UNKNOWN: ASK_RAPPO,
    __mismatch: '같은 회원에 다른 이유가 있으면 그것부터 처리해 주세요. 그래도 안 맞으면 라포에 회원 이름을 알려 주세요.',
    LESSON_BEFORE_START: '먼저 수업하고 나중에 결제했다면 그대로 두셔도 돼요. 결제일을 잘못 넣었다면 결제 내역에서 고쳐 주세요.',
    SCHEDULE_BEFORE_ISSUE: '지난 수업을 나중에 등록했다면 그대로 두셔도 돼요.',
    SCHEDULE_AFTER_END: '끝난 수강권 뒤에 잡힌 수업이 맞는지 스케줄에서 봐 주세요.',
    ISSUE_KIND_MISMATCH: '신규·재등록 표시가 맞는지 봐 주세요. 이 센터에서 정산 기준을 고칠 수 있으면 수강권 정보 창에 바꾸는 칸이 보여요.',
    ISSUE_DATE_AMBIGUOUS: '첫 결제일이 맞는지 결제 내역에서 봐 주세요.',
    END_BEFORE_START: '수강권 시작일과 끝난 날이 맞는지 봐 주세요.',
    TRANSFER_TARGET_NO_EFFECT: '양도받은 수강권이 쓸 수 있는 상태인지 봐 주세요.',
    COMPANION_COUNTERPART_CHANGE: '1:2 상대 수강권의 회차와 혼자 한 수업이 맞는지 봐 주세요.',
    COMPANION_PRIMARY_NOT_ALIVE: '1:2 대표 수강권이 남아 있는지 봐 주세요.',
    COMPANION_DATA_INCONSISTENT: '1:2 수강권 중 대표가 하나만 정해져 있는지 봐 주세요.',
    LESSON_MOVED_AFTER_CUTOFF: '급여를 이미 드렸다면 차액을 맞춰 주세요.',
    OTHER_COACH_LESSON: '대신 들어간 수업이면 이 표시 자체는 할 일이 없어요.',
    UNLINKED_HANDOFF: '예전 방식으로 강사를 바꿔서 붙는 표시예요. 이 표시 자체는 할 일이 없어요.',
    HANDOFF_COPY_NOT_FOUND: '이 표시 자체는 할 일이 없어요.',
    END_ESTIMATED: '이 표시 자체는 할 일이 없어요.',
    POST_END_SESSION_CHANGE: '이 표시 자체는 할 일이 없어요.',
    LEGACY_CANCELED_PAYMENT_ROW: '이 표시 자체는 할 일이 없어요.',
    CANCELED_AT_MISSING: '이 표시 자체는 할 일이 없어요.',
    COMPANION_REPRESENTATIVE_CHANGED: '이 표시 자체는 할 일이 없어요.',
    CORRECTED_AFTER_CUTOFF: '지난번에 받은 엑셀과 숫자가 다를 수 있어요. 이 표시 자체는 할 일이 없어요.',
    DELETED_AFTER_PERIOD: '이 표시 자체는 할 일이 없어요.',
    SESSIONLESS: '이 표시 자체는 할 일이 없어요.',
    SERVICE_SESSION: '이 표시 자체는 할 일이 없어요.',
};
/** 강사·센터 요약에서 가장 급한 단계(손볼 일 칸 색). 무시·안내용 플래그는 빼고, 손볼 회원이 없으면 null. */
export const topReviewLevel = (
    reference: ReviewReference,
): ReviewLevel | null => {
    const keys = Object.entries(checkFlagCounts(reference))
        .filter(([flag, n]) => n > 0 && !IGNORED_FLAGS.has(flag) && !INFO_ONLY_FLAGS.has(flag))
        .map(([flag]) => flag);
    if (mismatchCount(reference) > 0) keys.push('__mismatch');
    if (unpaidReviewCount(reference) > 0) keys.push('UNPAID_UNKNOWN');
    if (keys.length === 0) return null;
    return REVIEW_LEVEL_ORDER.find((level) => keys.some((k) => flagReviewLevel(k) === level)) ?? null;
};
/** 강사 변경(인계) 관련 이유에 붙이는 한 줄 설명 — 「수강권이 두 개」라는 개념을 모르면 이유를 읽을 수 없다. */
export const HANDOFF_FLAGS = new Set(['HANDOFF_CLEANUP_INCOMPLETE', 'UNLINKED_HANDOFF', 'HANDOFF_COPY_NOT_FOUND', 'LINEAGE_INVALID', 'UNPAID_UNKNOWN']);
export const HANDOFF_EXPLAINER = '라포는 담당 강사를 바꾸면 새 강사 이름으로 수강권을 새로 만들고, 바꾸기 전 수강권은 닫아요.';
export const describeFlagAction = (flag: string) => FLAG_ACTION[flag] ?? '수강권 기록을 열어 봐 주세요. 이상하면 라포에 알려 주세요.';

/** 화면 배지에서만 쓰는 이유 목록. 미수 null과 검산 차이를 빠뜨리거나 중복하지 않아요. */
export const rowReviewReasons = (row: Pick<SessionReportRow, 'check' | 'unpaidAmount'>) => {
    const flags = Array.from(new Set(visibleFlags(row.check?.flags)));
    if (row.unpaidAmount === null && !flags.includes('UNPAID_UNKNOWN')) flags.push('UNPAID_UNKNOWN');
    if (row.check?.status === 'MISMATCH') flags.unshift('__mismatch');
    return flags.sort((a, b) => REVIEW_LEVEL_ORDER.indexOf(flagReviewLevel(a)) - REVIEW_LEVEL_ORDER.indexOf(flagReviewLevel(b)));
};

/** 화면에서만 쓰는 짧은 안내예요. 엑셀 문구와 문제 분류는 그대로 둬요. */
const REVIEW_ISSUE: Record<string, string> = {
    OTHER_COACH_LESSON: '다른 강사가 한 수업이 있어요',
    UNLINKED_HANDOFF: '강사 변경 전 수강권의 연결 기록이 없어요',
    HANDOFF_COPY_NOT_FOUND: '강사 변경 후 수강권을 찾지 못했어요',
    END_ESTIMATED: '수강권이 없어진 시각을 추정했어요',
    POST_END_SESSION_CHANGE: '수강권이 끝난 뒤 회차가 바뀌었어요',
    LEGACY_CANCELED_PAYMENT_ROW: '예전 방식으로 취소된 결제는 뺐어요',
    CANCELED_AT_MISSING: '수강권 발급 취소 시각을 추정했어요',
    COMPANION_REPRESENTATIVE_CHANGED: '1:2 대표 수강권이 지난달과 달라요',
    CORRECTED_AFTER_CUTOFF: '집계한 뒤 수강권 정보를 고쳤어요',
    DELETED_AFTER_PERIOD: '이 달 수업 중 월말이 지난 뒤 삭제된 것이 있어요',
    SESSIONLESS: '무제한 수강권은 회차 합계에서 뺐어요',
    SERVICE_SESSION: '서비스로 드린 무료 회차예요',
    __mismatch: '기록과 수강권의 남은 회차가 달라요',
    NEGATIVE_REMAINING: '남은 회차가 마이너스예요',
    COMPANION_DUPLICATE_LESSON: '같은 시각에 수업이 두 번 잡혀 있어요',
    HANDOFF_CLEANUP_INCOMPLETE: '강사 변경 전 수강권이 남아 있어요',
    TRANSFER_UNPROCESSED: '양도한 회차가 줄었는지 확인이 필요해요',
    LINEAGE_INVALID: '강사 변경 전후의 수강권 연결이 맞지 않아요',
    UNPAID_UNKNOWN: '미수를 계산할 수 없어요',
    LESSON_BEFORE_START: '결제 전에 한 수업이 있어요',
    SCHEDULE_BEFORE_ISSUE: '발급 전에 한 지난달 이전 수업이 있어요',
    SCHEDULE_AFTER_END: '수강권이 끝난 뒤에 한 수업이 있어요',
    ISSUE_KIND_MISMATCH: '신규·재등록 표시와 발급 기록이 달라요',
    ISSUE_DATE_AMBIGUOUS: '발급일 확인이 필요해요',
    END_BEFORE_START: '끝난 날이 시작한 날보다 앞서요',
    TRANSFER_TARGET_NO_EFFECT: '양도받은 수강권을 아직 쓸 수 없어요',
    COMPANION_COUNTERPART_CHANGE: '1:2 상대 회차가 바뀌었거나 혼자 한 수업이 있어요',
    COMPANION_PRIMARY_NOT_ALIVE: '집계 시점에 남아 있는 1:2 대표 수강권이 없어요',
    COMPANION_DATA_INCONSISTENT: '1:2 대표 표시가 둘이에요',
    LESSON_MOVED_AFTER_CUTOFF: '집계한 뒤 수업을 다른 수강권으로 옮겼어요',
};
const REVIEW_ACTION: Record<string, string> = {
    __mismatch: '버튼 하나로 라포에 알려 주세요',
    NEGATIVE_REMAINING: '수강권 정보에서 지금 남은 회차를 확인해 주세요',
    COMPANION_DUPLICATE_LESSON: '스케줄에서 중복 수업 하나를 지워 주세요',
    LESSON_BEFORE_START: '먼저 수업하고 나중에 결제했다면 그대로 두세요',
    SCHEDULE_BEFORE_ISSUE: '지난 수업을 나중에 등록했다면 그대로 두세요',
    SCHEDULE_AFTER_END: '스케줄에서 수업 기록을 확인해 주세요',
    ISSUE_KIND_MISMATCH: '수강권의 신규·재등록 표시를 확인해 주세요',
    ISSUE_DATE_AMBIGUOUS: '결제 내역에서 첫 결제일을 확인해 주세요',
    END_BEFORE_START: '수강권의 시작일과 끝난 날을 확인해 주세요',
    TRANSFER_TARGET_NO_EFFECT: '양도받은 수강권의 상태를 확인해 주세요',
    COMPANION_COUNTERPART_CHANGE: '상대 수강권과 혼자 한 수업을 확인해 주세요',
    COMPANION_PRIMARY_NOT_ALIVE: '1:2 대표 수강권이 남아 있는지 확인해 주세요',
    COMPANION_DATA_INCONSISTENT: '1:2 대표가 하나인지 확인해 주세요',
    LESSON_MOVED_AFTER_CUTOFF: '급여를 이미 드렸다면 차액을 맞춰 주세요',
};
export const describeReviewIssue = (flag: string) => REVIEW_ISSUE[flag] ?? describeFlag(flag);
export const describeReviewAction = (flag: string) => REVIEW_ACTION[flag]
    ?? (flagReviewLevel(flag) === 'ask' ? '라포에 회원 이름을 알려 주세요'
        : flagReviewLevel(flag) === 'info' ? describeFlagAction(flag) : '수강권 기록을 확인해 주세요');
